/**
 * Mentor Engagement API client — WS-2 endpoint contract.
 *
 * Router prefix: `/api/mentor/engagements` (Tier 3 mentor, or b2b2c mentor/admin).
 * Every method mirrors an endpoint from the spec 1:1; nothing is invented except
 * `POST …/crf/reopen`, which the UI needs for the "final record → Reopen" rule
 * but which the endpoint list omits (flagged for the backend slice).
 *
 * Set `EXPO_PUBLIC_MENTOR_FORMS_MOCK=1` to run the whole workspace against the
 * in-memory fixtures in `mentorEngagementsMock.ts`.
 */

import { apiRequest } from './apiClient';
import api from '../api';
import type { mentorEngagementsMock as MentorEngagementsMock } from './mentorEngagementsMock';
import type {
  EngagementOverview,
  PrioritiesUpdateRequest,
  CompetencyReviewForm,
  CrfFinalizeRequest,
  CrfNarrativeResponse,
  CrfUpdateRequest,
  EngagementCreateRequest,
  EngagementDetail,
  EngagementSendRequest,
  EngagementSendResponse,
  EngagementSessionRow,
  EngagementStatus,
  EngagementSummary,
  EngagementTrajectory,
  EngagementUpdateRequest,
  FinalizeRequest,
  MentorSetupChecklist,
  SessionAttachRequest,
  SessionObservationForm,
  SessionTranscript,
  SessionUpdateRequest,
  SofUpdateRequest,
} from '../../types/mentorEngagements';

// Fixture mode is dev-only. __DEV__ comes first so production builds fold the
// flag to false, and the fixture module is only reached through the
// __DEV__-gated dynamic import in loadMock(), so it is not part of the
// production entry bundle (same pattern as icfProgressAPI, FE-11).
const MENTOR_FORMS_MOCK_ENABLED = __DEV__ && process.env.EXPO_PUBLIC_MENTOR_FORMS_MOCK === '1';

async function loadMock(): Promise<typeof MentorEngagementsMock> {
  // The import sits inside the __DEV__ block so production transforms fold
  // the whole block away before dependency collection.
  if (__DEV__) {
    const { mentorEngagementsMock } = await import('./mentorEngagementsMock');
    return mentorEngagementsMock;
  }
  throw new Error('Mentor forms mock is dev-only');
}

const BASE = '/mentor/engagements';
/** `api` already prefixes nothing, so blob URLs need the explicit /api segment. */
const BLOB_BASE = '/api/mentor/engagements';

async function downloadPdf(url: string, language?: string): Promise<Blob> {
  const response = await api.get(url, {
    params: language ? { language } : undefined,
    responseType: 'blob',
  });
  return response.data as Blob;
}

export const MentorEngagementsAPI = {
  isMockEnabled: MENTOR_FORMS_MOCK_ENABLED,

  // ── Engagements ────────────────────────────────────────────────────────────

  list(status?: EngagementStatus): Promise<EngagementSummary[]> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.list(status));
    return apiRequest<EngagementSummary[]>({
      endpoint: BASE,
      method: 'GET',
      params: status ? { status } : undefined,
    });
  },

  /** The mentor setup checklist, derived server-side from the mentor's own rows. Fixture mode has none. */
  getSetupChecklist(): Promise<MentorSetupChecklist> {
    if (MENTOR_FORMS_MOCK_ENABLED) {
      return Promise.resolve({ applicable: false, steps: [], completed: 0, total: 0, next_step: null });
    }
    return apiRequest<MentorSetupChecklist>({ endpoint: `${BASE}/setup-checklist`, method: 'GET' });
  },

  get(engagementId: number): Promise<EngagementDetail> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.get(engagementId));
    return apiRequest<EngagementDetail>({ endpoint: `${BASE}/${engagementId}`, method: 'GET' });
  },

  getOverview(engagementId: number): Promise<EngagementOverview> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.getOverview(engagementId));
    return apiRequest<EngagementOverview>({ endpoint: `${BASE}/${engagementId}/overview`, method: 'GET' });
  },

  updatePriorities(engagementId: number, payload: PrioritiesUpdateRequest): Promise<EngagementDetail> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.updatePriorities(engagementId, payload));
    return apiRequest<EngagementDetail, PrioritiesUpdateRequest>({ endpoint: `${BASE}/${engagementId}/priorities`, method: 'PUT', data: payload });
  },

  create(payload: EngagementCreateRequest): Promise<EngagementDetail> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.create(payload));
    return apiRequest<EngagementDetail, EngagementCreateRequest>({
      endpoint: BASE,
      method: 'POST',
      data: payload,
    });
  },

  update(engagementId: number, payload: EngagementUpdateRequest): Promise<EngagementDetail> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.update(engagementId, payload));
    return apiRequest<EngagementDetail, EngagementUpdateRequest>({
      endpoint: `${BASE}/${engagementId}`,
      method: 'PATCH',
      data: payload,
    });
  },

  remove(engagementId: number): Promise<void> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.remove(engagementId));
    return apiRequest<void>({ endpoint: `${BASE}/${engagementId}`, method: 'DELETE' });
  },

  // ── Sessions ───────────────────────────────────────────────────────────────

  addSession(engagementId: number, payload: SessionAttachRequest): Promise<EngagementSessionRow> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.addSession(engagementId, payload));
    return apiRequest<EngagementSessionRow, SessionAttachRequest>({
      endpoint: `${BASE}/${engagementId}/sessions`,
      method: 'POST',
      data: payload,
    });
  },

  updateSession(
    engagementId: number,
    sessionId: number,
    payload: SessionUpdateRequest,
  ): Promise<EngagementSessionRow> {
    if (MENTOR_FORMS_MOCK_ENABLED) {
      return loadMock().then((mock) => mock.updateSession(engagementId, sessionId, payload));
    }
    return apiRequest<EngagementSessionRow, SessionUpdateRequest>({
      endpoint: `${BASE}/${engagementId}/sessions/${sessionId}`,
      method: 'PATCH',
      data: payload,
    });
  },

  removeSession(engagementId: number, sessionId: number): Promise<void> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.removeSession(engagementId, sessionId));
    return apiRequest<void>({
      endpoint: `${BASE}/${engagementId}/sessions/${sessionId}`,
      method: 'DELETE',
    });
  },

  reanalyzeSession(engagementId: number, sessionId: number): Promise<{ task_id?: string }> {
    return apiRequest<{ task_id?: string }>({
      endpoint: `${BASE}/${engagementId}/sessions/${sessionId}/reanalyze`,
      method: 'POST',
    });
  },

  // ── Session Observation Form ───────────────────────────────────────────────

  getSof(engagementId: number, sessionId: number): Promise<SessionObservationForm> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.getSof(engagementId, sessionId));
    return apiRequest<SessionObservationForm>({
      endpoint: `${BASE}/${engagementId}/sessions/${sessionId}/sof`,
      method: 'GET',
    });
  },

  updateSof(
    engagementId: number,
    sessionId: number,
    payload: SofUpdateRequest,
  ): Promise<SessionObservationForm> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.updateSof(engagementId, sessionId, payload));
    return apiRequest<SessionObservationForm, SofUpdateRequest>({
      endpoint: `${BASE}/${engagementId}/sessions/${sessionId}/sof`,
      method: 'PUT',
      data: payload,
    });
  },

  finalizeSof(
    engagementId: number,
    sessionId: number,
    payload: FinalizeRequest,
  ): Promise<SessionObservationForm> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.finalizeSof(engagementId, sessionId, payload));
    return apiRequest<SessionObservationForm, FinalizeRequest>({
      endpoint: `${BASE}/${engagementId}/sessions/${sessionId}/sof/finalize`,
      method: 'POST',
      data: payload,
    });
  },

  reopenSof(engagementId: number, sessionId: number): Promise<SessionObservationForm> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.reopenSof(engagementId, sessionId));
    return apiRequest<SessionObservationForm>({
      endpoint: `${BASE}/${engagementId}/sessions/${sessionId}/sof/reopen`,
      method: 'POST',
    });
  },

  /**
   * Read-only transcript window for the SOF evidence "Jump" side-sheet.
   * The mentor-scoped endpoint lands with the backend slice; callers treat a
   * failure as "transcript not available" rather than an error.
   */
  getSessionTranscript(engagementId: number, sessionId: number): Promise<SessionTranscript> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.getSessionTranscript(engagementId, sessionId));
    return apiRequest<SessionTranscript>({
      endpoint: `${BASE}/${engagementId}/sessions/${sessionId}/transcript`,
      method: 'GET',
    });
  },

  sofPdf(engagementId: number, sessionId: number, language?: string): Promise<Blob> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.sofPdf());
    return downloadPdf(`${BLOB_BASE}/${engagementId}/sessions/${sessionId}/sof/pdf`, language);
  },

  // ── Competency Review Form ─────────────────────────────────────────────────

  getCrf(engagementId: number): Promise<CompetencyReviewForm> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.getCrf(engagementId));
    return apiRequest<CompetencyReviewForm>({ endpoint: `${BASE}/${engagementId}/crf`, method: 'GET' });
  },

  updateCrf(engagementId: number, payload: CrfUpdateRequest): Promise<CompetencyReviewForm> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.updateCrf(engagementId, payload));
    return apiRequest<CompetencyReviewForm, CrfUpdateRequest>({
      endpoint: `${BASE}/${engagementId}/crf`,
      method: 'PUT',
      data: payload,
    });
  },

  generateCrfNarrative(engagementId: number): Promise<CrfNarrativeResponse> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.generateNarrative(engagementId));
    return apiRequest<CrfNarrativeResponse>({
      endpoint: `${BASE}/${engagementId}/crf/narrative`,
      method: 'POST',
    });
  },

  finalizeCrf(engagementId: number, payload: CrfFinalizeRequest): Promise<CompetencyReviewForm> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.finalizeCrf(engagementId, payload));
    return apiRequest<CompetencyReviewForm, CrfFinalizeRequest>({
      endpoint: `${BASE}/${engagementId}/crf/finalize`,
      method: 'POST',
      data: payload,
    });
  },

  /** Not in the WS-2 endpoint list — required by the "final records are read-only with Reopen" rule. */
  reopenCrf(engagementId: number): Promise<CompetencyReviewForm> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.reopenCrf(engagementId));
    return apiRequest<CompetencyReviewForm>({
      endpoint: `${BASE}/${engagementId}/crf/reopen`,
      method: 'POST',
    });
  },

  crfPdf(engagementId: number, language?: string): Promise<Blob> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.crfPdf());
    return downloadPdf(`${BLOB_BASE}/${engagementId}/crf/pdf`, language);
  },

  // ── Delivery + trajectory ──────────────────────────────────────────────────

  send(engagementId: number, payload: EngagementSendRequest): Promise<EngagementSendResponse> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.send(engagementId, payload));
    return apiRequest<EngagementSendResponse, EngagementSendRequest>({
      endpoint: `${BASE}/${engagementId}/send`,
      method: 'POST',
      data: payload,
    });
  },

  trajectory(engagementId: number): Promise<EngagementTrajectory> {
    if (MENTOR_FORMS_MOCK_ENABLED) return loadMock().then((mock) => mock.trajectory(engagementId));
    return apiRequest<EngagementTrajectory>({
      endpoint: `${BASE}/${engagementId}/trajectory`,
      method: 'GET',
    });
  },
};

export default MentorEngagementsAPI;
